import { Service, service, inject } from './di'
import { Response, Request } from '../utils/Router'
import { i18n, ll } from '../i18n/I18n'
import { itemPopTitle } from '../utils/modal'

import { FlyerService } from './FlyerService'
import { LocationService } from './LocationService'
import { CategoryService } from './CategoryService'
import { BlackFridayService } from './BlackFridayService'
import { ExpiredMetadaService } from './ExpiredMetadaService'
import { Coupon } from '../definitions/CouponDefinitions'
import { Flyer, FlyerItemDetails } from '../definitions/FlyerDefinitions'

@service
export default class MetaTitleAndDescService extends Service {
    @inject location: LocationService
    @inject categoryService: CategoryService
    @inject bf: BlackFridayService
    @inject expiredMetadaService: ExpiredMetadaService
    @inject flyerService: FlyerService

    setNotFound(res: Response) {
        res.title = res.title ? res.title : 'Error 404 (Not Found) - Flipp'
    }

    setWatchlist(res: Response) {
        res.title = i18n('watchlist_page_title')
    }

    setBlackFriday(res: Response) {
        if (this.bf.isEnabled()) {
            res.title += this.getBlackFridayTitle()
            res.description += this.getBlackFridayDescription()
        }
    }

    getBlackFridayTitle() {
        return i18n('bf_title')
    }

    getBlackFridayDescription() {
        return i18n('bf_description')
    }

    setCoupon(req: Request, res: Response) {
        const category = req.params['category'] || 'all-deals'
        const couponSlugToName = this.categoryService.getCouponsToSlugMapping()
        const categoryName = couponSlugToName[category]
        const city = this.location.city

        if (category === 'all-deals') {
            res.title = i18n('coupons_title') + ' - Flipp'
            res.description = i18n('coupons_description', { city })
        } else {
            res.title =
                i18n('coupons_category_title', {
                    city,
                    category: categoryName,
                }) + ' - Flipp'
            res.description = i18n('coupons_category_description', {
                category: categoryName,
            })
        }
        this.setBlackFriday(res)
    }

    setCoupons(req: Request, res: Response) {
        const category = req.params['category'] || 'all-deals'
        const couponSlugToName = this.categoryService.getCouponsToSlugMapping()
        const categoryName = couponSlugToName[category]
        const city =
            this.location.city === 'null'
                ? this.location.postalCode
                : this.location.city

        const state = this.location.getStateName({
            slug: this.location.state,
        })
        if (category === 'all-deals') {
            res.title = i18n('index_city_coupon_title', { city, state })
            res.description = i18n('index_city_coupon_description', {
                city,
                state,
            })
        } else {
            res.title = i18n('index_coupon_category_title', {
                city,
                category: i18n(categoryName),
            })
            res.description = i18n('index_coupon_category_description', {
                city,
                state,
                category: i18n(categoryName),
            })
        }
        this.setBlackFriday(res)
    }

    setCouponPage(res: Response) {
        res.title = ExpiredMetadaService.getInstance().getCouponTitle()
        res.description =
            ExpiredMetadaService.getInstance().getCouponDescription()
        this.setBlackFriday(res)
    }

    setCouponRouting(res: Response, coupon: Coupon) {
        const couponSeoData = {
            store: coupon.brand ? coupon.brand : coupon.merchantName,
            discount: coupon.getDiscount(),
            name: coupon.getName(),
            city: this.location.city,
        }
        res.title = i18n('coupon_page_title', couponSeoData)
        res.description = i18n('coupon_page_description', couponSeoData)
        this.setBlackFriday(res)
    }

    setShoppingList(res: Response) {
        res.title = i18n('shopping_list_title')
        this.setBlackFriday(res)
    }

    setFavourites(res: Response) {
        res.title = i18n('fav_title')
        this.setBlackFriday(res)
    }

    setSearch(req: Request, res: Response) {
        const city = this.location.city
        const state = this.location.state
        const q = req.params['query'] || req.params['q']
        res.title = i18n('search_page_title', { city, q, state })
        res.description = i18n('search_page_description', { city, q })
        this.setBlackFriday(res)
    }

    setCountryHomePage(res: Response) {
        res.title = i18n('index_page_title')
        res.description = i18n('index_page_description')
        this.setBlackFriday(res)
    }

    setCityHomePage(res: Response) {
        const city = this.location.city
        res.title = i18n('index_city_title', { city: city })
        res.description = i18n('index_city_description', { city: city })
        this.setBlackFriday(res)
    }

    setItem(res: Response, item: any, flyer: Flyer) {
        res.title = itemPopTitle(item, flyer)
        res.description = item.description
        this.setBlackFriday(res)
    }

    async getTitle(flyerId: number, city: string) {
        const flyer = await this.flyerService.getFlyer(flyerId)
        if (!flyer) {
            return
        }

        const { merchant } = flyer
        return i18n('index_single_flyer_title', {
            city,
            name: flyer.getName(),
            merchant,
        })
    }

    async setListing(res: Response, flyer: Flyer) {
        const city = this.location.city
        res.title = await this.getTitle(flyer.id, city)
        res.description = i18n('flyer_browse_description', {
            city,
            merchant: flyer.merchant,
            from: ll(flyer.validFrom),
            to: ll(flyer.validTo),
        })
        this.setBlackFriday(res)
    }

    async getFlyerDescription(city: string, flyerId: number) {
        const flyer = await this.flyerService.getFlyer(flyerId)
        if (!flyer) {
            return
        }
        const { merchant } = flyer
        const validity = `Valid ${ll(flyer.validFrom)} to ${ll(flyer.validTo)}`
        return i18n('index_single_flyer_description', {
            city,
            merchant,
            name: flyer.getName(),
            validity,
        })
    }

    async setFlyer(res: Response, flyer: Flyer) {
        res.title = await this.getTitle(flyer.id, this.location.city)
        res.description = await this.getFlyerDescription(
            this.location.city,
            flyer.id
        )
        this.setBlackFriday(res)
    }

    setHomePage(res: Response) {
        res.title = i18n('index_page_title')
        res.description = i18n('index_page_description')
        this.setBlackFriday(res)
    }

    setMerchant(res: Response, store: string) {
        res.title = i18n('store_flyers_title', { store })
        res.description = i18n('store_flyers_description', { store })
        this.setBlackFriday(res)
    }

    setExpiredItem(res: Response) {
        res.title = ExpiredMetadaService.getInstance().getItemTitle()
        res.description =
            ExpiredMetadaService.getInstance().getItemDescription()
        this.setBlackFriday(res)
    }

    setItemPage(res: Response, item: FlyerItemDetails) {
        const city =
            this.location.city === 'null'
                ? this.location.postalCode
                : this.location.city
        const { currentPrice, merchant, name } = item
        res.title = i18n('index_item_title', {
            price: currentPrice,
            item: name,
            merchant,
            city,
        })
        res.description = item.description
        this.setBlackFriday(res)
    }

    setFlyers(res: Response, category: string, city: string) {
        const categorySlugToName =
            this.categoryService.getCategorySlugToNameMapping()
        const categoryName = categorySlugToName[category]
        if (category === 'all-flyers') {
            res.title = i18n('index_flyers_title', { city })
            res.description = i18n('index_flyers_description', { city })
        } else {
            res.title = i18n('category_title', {
                city,
                category: i18n(categoryName),
            })
            res.description = i18n('category_description', {
                city,
                category: i18n(categoryName),
            })
        }
        this.setBlackFriday(res)
    }

    setBlackFridayPage(res: Response) {
        res.title = i18n('blackfriday_title')
        res.description = i18n('blackfriday_description')
        this.setBlackFriday(res)
    }
}
